import { z } from 'zod';

const text = (max: number, msg?: string) => z.string().trim().min(1, msg ? { error: msg } : undefined).max(max);
const optText = (max: number) => z.string().trim().max(max).optional().transform((v) => v || null);
const isoDate = z.iso.date({ error: 'Pick a date.' });

/* Tasks */
export const TASK_PRIORITIES = ['low', 'normal', 'high'] as const;
export const TASK_STATUSES = ['todo', 'in_progress', 'done'] as const;
export const TASK_STATUS_LABELS = { todo: 'To do', in_progress: 'Doing', done: 'Done' } as const;
export const taskSchema = z.object({
  title: text(200, 'What needs to be done?'),
  description: optText(5000),
  assignedTo: z.uuid().optional(),
  leadId: z.uuid().nullable().optional(),
  dueAt: z.iso.datetime({ offset: true }).nullable().optional(),
  priority: z.enum(TASK_PRIORITIES).default('normal'),
});
/** Edits: only the fields sent are changed (no defaults, so nothing is reset by accident). */
export const taskPatchSchema = z.object({
  title: text(200, 'What needs to be done?').optional(),
  description: z.string().trim().max(5000).nullable().optional().transform((v) => (v === undefined ? undefined : v || null)),
  assignedTo: z.uuid().optional(),
  leadId: z.uuid().nullable().optional(),
  dueAt: z.iso.datetime({ offset: true }).nullable().optional(),
  priority: z.enum(TASK_PRIORITIES).optional(),
  status: z.enum(TASK_STATUSES).optional(),
});

export const dayReportSchema = z.object({ day: isoDate, plan: optText(5000), done: optText(5000) });

/* Leaves */
export const LEAVE_TYPES = ['casual', 'sick', 'earned', 'unpaid'] as const;
export type LeaveType = (typeof LEAVE_TYPES)[number];
export const LEAVE_LABELS: Record<LeaveType, string> = { casual: 'Casual leave', sick: 'Sick leave', earned: 'Earned leave', unpaid: 'Unpaid leave' };
export const leaveRequestSchema = z
  .object({
    leaveType: z.enum(LEAVE_TYPES),
    fromDate: isoDate,
    toDate: isoDate,
    halfDay: z.boolean().default(false),
    reason: text(500, 'Write a short reason.'),
  })
  .refine((l) => l.toDate >= l.fromDate, { error: 'The end date is before the start date.', path: ['toDate'] })
  .refine((l) => !l.halfDay || l.fromDate === l.toDate, { error: 'A half day must be a single date.', path: ['halfDay'] });
export const decisionSchema = z.object({ approve: z.boolean(), note: optText(300) });

/** Paid leave days each person gets per year (Settings → HR). */
export const leavePolicySchema = z.object({
  casual: z.coerce.number().min(0).max(60),
  sick: z.coerce.number().min(0).max(60),
  earned: z.coerce.number().min(0).max(60),
  weeklyOff: z.array(z.number().int().min(1).max(7)).max(7),
});
export type LeavePolicy = z.infer<typeof leavePolicySchema>;
export const DEFAULT_LEAVE_POLICY: LeavePolicy = { casual: 12, sick: 12, earned: 15, weeklyOff: [7] };

/* Expenses */
export const EXPENSE_CATEGORIES = ['travel', 'food', 'stay', 'phone', 'other'] as const;
export const EXPENSE_LABELS = { travel: 'Travel', food: 'Food', stay: 'Hotel / stay', phone: 'Phone / internet', other: 'Other' } as const;
export const expenseSchema = z.object({
  spentOn: isoDate,
  category: z.enum(EXPENSE_CATEGORIES),
  km: z.coerce.number().min(0).max(5000).nullable().optional(),
  amount: z.coerce.number({ error: 'Enter the amount.' }).positive({ error: 'Enter the amount.' }).max(1_000_000),
  description: text(500, 'What was it for?'),
  receiptFileId: z.uuid().nullable().optional(),
});

/* Employee profile (HR) */
export const employeeProfileSchema = z.object({
  department: optText(100),
  joiningDate: isoDate.nullable().optional(),
  monthlySalary: z.coerce.number().min(0).max(10_000_000).nullable().optional(),
  bankName: optText(100),
  accountNumber: z.string().trim().regex(/^\d{6,20}$/, { error: 'Account number should be 6–20 digits.' }).optional().or(z.literal('').transform(() => undefined)),
  ifsc: z.string().trim().toUpperCase().regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, { error: 'IFSC looks like HDFC0001234.' }).optional().or(z.literal('').transform(() => null)),
  upiId: z.string().trim().regex(/^[\w.-]+@[\w.-]+$/, { error: 'UPI ID looks like name@bank.' }).optional().or(z.literal('').transform(() => null)),
  pan: z.string().trim().toUpperCase().regex(/^[A-Z]{5}\d{4}[A-Z]$/, { error: 'PAN looks like ABCDE1234F.' }).optional().or(z.literal('').transform(() => null)),
  emergencyContact: optText(200),
});

/* Salary slip edits */
export const slipPatchSchema = z.object({
  bonus: z.coerce.number().min(0).max(10_000_000).optional(),
  otherDeductions: z.coerce.number().min(0).max(10_000_000).optional(),
  note: optText(500),
});
